// Chakra imports
import {
  Box,
  Text,
  SimpleGrid,
  useColorModeValue,
  Icon,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  Card,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Divider,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  Switch,
  useToast,
  InputGroup,
  InputRightElement,
  IconButton,
} from "@chakra-ui/react";
// Custom components
import MiniStatistics from "components/card/MiniStatistics";
import IconBox from "components/icons/IconBox";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  MdMonitor,
  MdSpeed,
  MdTrendingUp,
  MdInfo,
  MdAdd,
  MdLink,
  MdContentCopy,
} from "react-icons/md";
import { formatNumber, formatDate } from "utils/format";
import systemService from "services/systemService";

export default function Monitoring() {
  const navigate = useNavigate();
  
  // Chakra Color Mode - All hooks must be called at the top level
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary = useColorModeValue('secondaryGray.600', 'white');
  const cardBg = useColorModeValue('white', 'navy.800');
  const brandColor = useColorModeValue('brand.500', 'white');
  const boxBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');
  const borderColor = useColorModeValue('secondaryGray.200', 'whiteAlpha.100');
  const badgeBg = useColorModeValue('secondaryGray.100', 'whiteAlpha.100');
  const rowHoverBg = useColorModeValue('secondaryGray.50', 'whiteAlpha.50');

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [dailyReports, setDailyReports] = useState([]);
  const [reportsPagination, setReportsPagination] = useState(null);
  const [loadingReports, setLoadingReports] = useState(false);
  const [reportFiles, setReportFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [runningScheduler, setRunningScheduler] = useState(false);

  // Generate Link State
  const [generatingLink, setGeneratingLink] = useState(false);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [linkExpiresIn, setLinkExpiresIn] = useState(60);
  const [linkError, setLinkError] = useState(null);
  
  const { isOpen: isLinkModalOpen, onOpen: onLinkModalOpen, onClose: onLinkModalClose } = useDisclosure();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch recipients
        const recipientsResult = await systemService.getMonitoringRecipients({ skip: 0, limit: 100 });
        if (recipientsResult.success && recipientsResult.data) {
          setRecipients(recipientsResult.data);
          setPagination(recipientsResult.pagination);
        } else {
          setError(recipientsResult.error || 'Failed to load monitoring recipients');
        }

        // Fetch daily reports
        setLoadingReports(true);
        const reportsResult = await systemService.getDailyReports({ skip: 0, limit: 30 });
        if (reportsResult.success && reportsResult.data) {
          setDailyReports(reportsResult.data);
          setReportsPagination(reportsResult.pagination);
        }

        // Fetch report files
        setLoadingFiles(true);
        const filesResult = await systemService.getReportFiles();
        if (filesResult.success && filesResult.data) {
          setReportFiles(filesResult.data);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load monitoring data. Please try again.');
      } finally {
        setLoading(false);
        setLoadingReports(false);
        setLoadingFiles(false);
      }
    };

    fetchData();
  }, []);

  // Create Recipient State
  const [creating, setCreating] = useState(false);
  const [recipientFormData, setRecipientFormData] = useState({ email: '', name: '', is_active: true });
  const [formErrors, setFormErrors] = useState({});
  
  const toast = useToast();

  const handleRunScheduler = async () => {
    setRunningScheduler(true);
    try {
      const result = await systemService.runSchedulerReports();
      if (result.success) {
        const { data } = result;
        const hourly = data?.hourly || 'unknown';
        const daily = data?.daily || 'unknown';
        const errors = data?.errors || [];
        const status = result.status || 'success';
        toast({
          title: status === 'success' ? 'Scheduler completed' : 'Scheduler completed with issues',
          description: `Hourly: ${hourly}, Daily: ${daily}.${errors.length ? ` ${errors.join(' ')}` : ''}`,
          status: status === 'success' ? 'success' : 'warning',
          duration: errors.length ? 8000 : 5000,
          isClosable: true,
        });
        if (result.success && (hourly === 'completed' || daily === 'completed')) {
          setLoadingReports(true);
          const reportsResult = await systemService.getDailyReports({ skip: 0, limit: 30 });
          if (reportsResult.success && reportsResult.data) {
            setDailyReports(reportsResult.data);
            setReportsPagination(reportsResult.pagination);
          }
          const filesResult = await systemService.getReportFiles();
          if (filesResult.success && filesResult.data) setReportFiles(filesResult.data);
          setLoadingReports(false);
        }
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to run scheduler',
          status: 'error',
          duration: 6000,
          isClosable: true,
        });
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to run scheduler. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setRunningScheduler(false);
    }
  };

  // Modals
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();

  // Handle open create modal
  const handleOpenCreate = () => {
    setRecipientFormData({ email: '', name: '', is_active: true });
    setFormErrors({});
    onCreateOpen();
  };

  // Handle create recipient
  const handleCreateRecipient = async () => {
    // Validate
    const errors = {};
    if (!recipientFormData.email || !recipientFormData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientFormData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (!recipientFormData.name || !recipientFormData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setCreating(true);
    setFormErrors({});
    
    try {
      const result = await systemService.createMonitoringRecipient({
        email: recipientFormData.email.trim(),
        name: recipientFormData.name.trim(),
        is_active: recipientFormData.is_active,
      });
      
      if (result.success) {
        toast({
          title: 'Success',
          description: result.message || 'Recipient created successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        // Reset form
        setRecipientFormData({ email: '', name: '', is_active: true });
        setFormErrors({});
        onCreateClose();
        // Refresh recipients list
        const refreshResult = await systemService.getMonitoringRecipients({ skip: 0, limit: 100 });
        if (refreshResult.success && refreshResult.data) {
          setRecipients(refreshResult.data);
          setPagination(refreshResult.pagination);
        }
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to create recipient',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Error creating recipient:', err);
      toast({
        title: 'Error',
        description: 'Failed to create recipient. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCreating(false);
    }
  };

  // Handle generate monitor link
  const handleGenerateLink = async () => {
    if (linkExpiresIn < 1 || linkExpiresIn > 1440) {
      setLinkError('Expiration time must be between 1 and 1440 minutes (24 hours)');
      return;
    }

    setGeneratingLink(true);
    setLinkError(null);
    setGeneratedLink(null);
    
    try {
      const result = await systemService.generateMonitorLink(linkExpiresIn);
      
      if (result.success) {
        setGeneratedLink(result.data);
        toast({
          title: 'Success',
          description: result.message || 'Monitor link generated successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        setLinkError(result.error || 'Failed to generate monitor link');
        toast({
          title: 'Error',
          description: result.error || 'Failed to generate monitor link',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Error generating monitor link:', err);
      setLinkError('Failed to generate monitor link. Please try again.');
      toast({
        title: 'Error',
        description: 'Failed to generate monitor link. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setGeneratingLink(false);
    }
  };

  // Handle copy link to clipboard
  const handleCopyLink = async () => {
    if (generatedLink && generatedLink.link) {
      try {
        await navigator.clipboard.writeText(generatedLink.link);
        toast({
          title: 'Copied',
          description: 'Link copied to clipboard',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } catch (err) {
        console.error('Error copying to clipboard:', err);
        toast({
          title: 'Error',
          description: 'Failed to copy link to clipboard',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  // Handle close link modal
  const handleCloseLinkModal = () => {
    onLinkModalClose();
    setGeneratedLink(null);
    setLinkError(null);
    setLinkExpiresIn(60);
  };

  if (loading) {
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
          <Spinner size="xl" color="brand.500" />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <Alert status="error" borderRadius="15px">
          <AlertIcon />
          <Text color={textColor}>{error}</Text>
        </Alert>
      </Box>
    );
  }

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Flex direction='column' gap='20px'>
        <Flex align='center' justify='space-between' flexWrap='wrap' gap='15px' mb='10px'>
          <Text color={textColor} fontSize='2xl' fontWeight='700'>
            Monitoring
          </Text>
          <Button
            leftIcon={<Icon as={MdLink} />}
            onClick={() => {
              setGeneratedLink(null);
              setLinkError(null);
              setLinkExpiresIn(60);
              onLinkModalOpen();
            }}
            colorScheme='blue'
            variant='solid'
            size='md'>
            Generate Monitor Link
          </Button>
        </Flex>

        {/* Statistics Cards */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap='20px' mb='20px'>
          <MiniStatistics
            startContent={
              <IconBox
                w='56px'
                h='56px'
                bg={boxBg}
                icon={
                  <Icon w='32px' h='32px' as={MdMonitor} color={brandColor} />
                }
              />
            }
            name='System Status'
            value='Active'
          />
          <MiniStatistics
            startContent={
              <IconBox
                w='56px'
                h='56px'
                bg={boxBg}
                icon={
                  <Icon w='32px' h='32px' as={MdSpeed} color={brandColor} />
                }
              />
            }
            name='Activity'
            value='Normal'
          />
          <MiniStatistics
            startContent={
              <IconBox
                w='56px'
                h='56px'
                bg={boxBg}
                icon={
                  <Icon w='32px' h='32px' as={MdTrendingUp} color='green.500' />
                }
              />
            }
            name='Performance'
            value='Good'
          />
          <MiniStatistics
            startContent={
              <IconBox
                w='56px'
                h='56px'
                bg={boxBg}
                icon={
                  <Icon w='32px' h='32px' as={MdInfo} color={brandColor} />
                }
              />
            }
            name='Uptime'
            value='99.9%'
          />
        </SimpleGrid>

        {/* Monitoring Recipients */}
        <Card p='30px' bg={cardBg}>
          <Flex align='center' justify='space-between' mb='20px'>
            <Flex align='center' gap='10px'>
              <Icon as={MdMonitor} color={textColor} fontSize='xl' />
              <Text color={textColor} fontSize='xl' fontWeight='600'>
                Report Recipients
              </Text>
            </Flex>
            <Button
              size='sm'
              colorScheme='blue'
              leftIcon={<Icon as={MdAdd} />}
              onClick={handleOpenCreate}>
              Add Recipient
            </Button>
          </Flex>
          <Divider borderColor={borderColor} mb='20px' />
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py="40px">
              <Spinner size="lg" color="brand.500" />
            </Box>
          ) : error ? (
            <Alert status="error" borderRadius="8px">
              <AlertIcon />
              <Text color={textColor}>{error}</Text>
            </Alert>
          ) : (
            <Box overflowX='auto'>
              <Table variant='simple' color='secondaryGray.700'>
                <Thead>
                  <Tr>
                    <Th color={textColorSecondary} fontSize='sm' fontWeight='600'>
                      Name
                    </Th>
                    <Th color={textColorSecondary} fontSize='sm' fontWeight='600'>
                      Email
                    </Th>
                    <Th color={textColorSecondary} fontSize='sm' fontWeight='600'>
                      Status
                    </Th>
                    <Th color={textColorSecondary} fontSize='sm' fontWeight='600'>
                      Created At
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {recipients && recipients.length > 0 ? (
                    recipients.map((recipient) => (
                      <Tr 
                        key={recipient.id}
                        _hover={{ bg: rowHoverBg, cursor: 'pointer' }}
                        onClick={() => navigate(`/admin/recipient/${recipient.id}`)}
                      >
                        <Td>
                          <Text color={textColor} fontSize='sm' fontWeight='600'>
                            {recipient.name || 'N/A'}
                          </Text>
                        </Td>
                        <Td>
                          <Text color={textColor} fontSize='sm'>
                            {recipient.email}
                          </Text>
                        </Td>
                        <Td>
                          <Badge
                            colorScheme={recipient.is_active ? 'green' : 'red'}
                            bg={badgeBg}
                            color={textColor}
                            fontSize='xs'
                            fontWeight='500'
                            px='8px'
                            py='4px'
                            borderRadius='8px'
                          >
                            {recipient.is_active ? 'ACTIVE' : 'INACTIVE'}
                          </Badge>
                        </Td>
                        <Td>
                          <Text color={textColor} fontSize='sm'>
                            {recipient.created_at ? formatDate(recipient.created_at) : 'N/A'}
                          </Text>
                        </Td>
                      </Tr>
                    ))
                  ) : (
                    <Tr>
                      <Td colSpan={4} textAlign='center' py='20px'>
                        <Text color={textColorSecondary} fontSize='sm'>
                          No recipients found
                        </Text>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Box>
          )}
          {pagination && (
            <Text color={textColorSecondary} fontSize='xs' mt='15px' fontStyle='italic'>
              Total: {pagination.total} recipients
            </Text>
          )}
        </Card>

        {/* Daily Reports */}
        <Card p='30px' bg={cardBg}>
          <Flex align='center' justify='space-between' flexWrap='wrap' gap='10px' mb='20px'>
            <Flex align='center' gap='10px'>
              <Icon as={MdTrendingUp} color={textColor} fontSize='xl' />
              <Text color={textColor} fontSize='xl' fontWeight='600'>
                Daily Reports
              </Text>
            </Flex>
            <Button
              size='sm'
              colorScheme='brand'
              leftIcon={<Icon as={MdSpeed} />}
              onClick={handleRunScheduler}
              isLoading={runningScheduler}
              loadingText='Running'
            >
              Run scheduler report tasks
            </Button>
          </Flex>
          <Divider borderColor={borderColor} mb='20px' />
          {loadingReports ? (
            <Box display="flex" justifyContent="center" alignItems="center" py="40px">
              <Spinner size="lg" color="brand.500" />
            </Box>
          ) : dailyReports && dailyReports.length > 0 ? (
            <Box overflowX='auto'>
              <Table variant='simple' color='secondaryGray.700'>
                <Thead>
                  <Tr>
                    <Th color={textColorSecondary} fontSize='sm' fontWeight='600'>
                      Date
                    </Th>
                    <Th color={textColorSecondary} fontSize='sm' fontWeight='600'>
                      Total Requests
                    </Th>
                    <Th color={textColorSecondary} fontSize='sm' fontWeight='600'>
                      Successful
                    </Th>
                    <Th color={textColorSecondary} fontSize='sm' fontWeight='600'>
                      Failed
                    </Th>
                    <Th color={textColorSecondary} fontSize='sm' fontWeight='600'>
                      Unique Users
                    </Th>
                    <Th color={textColorSecondary} fontSize='sm' fontWeight='600'>
                      Avg Response (ms)
                    </Th>
                    <Th color={textColorSecondary} fontSize='sm' fontWeight='600'>
                      Status
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {dailyReports.map((report) => (
                    <Tr 
                      key={report.id}
                      _hover={{ bg: rowHoverBg, cursor: 'pointer' }}
                      onClick={() => navigate(`/admin/report/${report.date}`)}
                    >
                      <Td>
                        <Text color={textColor} fontSize='sm' fontWeight='600'>
                          {report.date ? formatDate(report.date) : 'N/A'}
                        </Text>
                      </Td>
                      <Td>
                        <Text color={textColor} fontSize='sm'>
                          {formatNumber(report.total_requests || 0)}
                        </Text>
                      </Td>
                      <Td>
                        <Badge
                          colorScheme='green'
                          bg={badgeBg}
                          color={textColor}
                          fontSize='xs'
                          fontWeight='500'
                          px='8px'
                          py='4px'
                          borderRadius='8px'
                        >
                          {formatNumber(report.successful_requests || 0)}
                        </Badge>
                      </Td>
                      <Td>
                        <Badge
                          colorScheme='red'
                          bg={badgeBg}
                          color={textColor}
                          fontSize='xs'
                          fontWeight='500'
                          px='8px'
                          py='4px'
                          borderRadius='8px'
                        >
                          {formatNumber(report.failed_requests || 0)}
                        </Badge>
                      </Td>
                      <Td>
                        <Text color={textColor} fontSize='sm'>
                          {formatNumber(report.unique_users || 0)}
                        </Text>
                      </Td>
                      <Td>
                        <Text color={textColor} fontSize='sm'>
                          {report.average_response_time_ms ? Math.round(report.average_response_time_ms) : 0} ms
                        </Text>
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={report.sent_at ? 'green' : 'gray'}
                          bg={badgeBg}
                          color={textColor}
                          fontSize='xs'
                          fontWeight='500'
                          px='8px'
                          py='4px'
                          borderRadius='8px'
                        >
                          {report.sent_at ? 'SENT' : 'PENDING'}
                        </Badge>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          ) : (
            <Box textAlign='center' py='40px'>
              <Text color={textColorSecondary} fontSize='sm'>
                No daily reports found
              </Text>
            </Box>
          )}
          {reportsPagination && (
            <Text color={textColorSecondary} fontSize='xs' mt='15px' fontStyle='italic'>
              Total: {reportsPagination.total} reports
            </Text>
          )}
        </Card>

        {/* Report Files */}
        <Card p='30px' bg={cardBg}>
          <Flex align='center' gap='10px' mb='20px'>
            <Icon as={MdInfo} color={textColor} fontSize='xl' />
            <Text color={textColor} fontSize='xl' fontWeight='600'>
              Report Files
            </Text>
          </Flex>
          <Divider borderColor={borderColor} mb='20px' />
          {loadingFiles ? (
            <Box display="flex" justifyContent="center" alignItems="center" py="40px">
              <Spinner size="lg" color="brand.500" />
            </Box>
          ) : reportFiles && reportFiles.length > 0 ? (
            <Box overflowX='auto'>
              <Table variant='simple' color='secondaryGray.700'>
                <Thead>
                  <Tr>
                    <Th color={textColorSecondary} fontSize='sm' fontWeight='600'>
                      Date
                    </Th>
                    <Th color={textColorSecondary} fontSize='sm' fontWeight='600'>
                      File Size
                    </Th>
                    <Th color={textColorSecondary} fontSize='sm' fontWeight='600'>
                      Created At
                    </Th>
                    <Th color={textColorSecondary} fontSize='sm' fontWeight='600'>
                      Actions
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {reportFiles.map((file) => (
                    <Tr key={file.date}>
                      <Td>
                        <Text color={textColor} fontSize='sm' fontWeight='600'>
                          {file.date ? formatDate(file.date) : 'N/A'}
                        </Text>
                      </Td>
                      <Td>
                        <Text color={textColor} fontSize='sm'>
                          {file.file_size ? `${(file.file_size / 1024).toFixed(2)} KB` : 'N/A'}
                        </Text>
                      </Td>
                      <Td>
                        <Text color={textColor} fontSize='sm'>
                          {file.created_at ? formatDate(file.created_at) : 'N/A'}
                        </Text>
                      </Td>
                      <Td>
                        {file.file_url && (
                          <Button
                            as="a"
                            href={file.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="sm"
                            leftIcon={<Icon as={MdLink} />}
                            colorScheme="blue"
                            variant="outline">
                            Download
                          </Button>
                        )}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          ) : (
            <Box textAlign='center' py='40px'>
              <Text color={textColorSecondary} fontSize='sm'>
                No report files found
              </Text>
            </Box>
          )}
          {reportFiles && reportFiles.length > 0 && (
            <Text color={textColorSecondary} fontSize='xs' mt='15px' fontStyle='italic'>
              Total: {reportFiles.length} file{reportFiles.length !== 1 ? 's' : ''}
            </Text>
          )}
        </Card>
      </Flex>

      {/* Create Recipient Modal */}
      <Modal isOpen={isCreateOpen} onClose={onCreateClose} size="md">
        <ModalOverlay />
        <ModalContent bg={cardBg}>
          <ModalHeader color={textColor}>Add Report Recipient</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex direction="column" gap="20px">
              <FormControl isRequired isInvalid={!!formErrors.name}>
                <FormLabel color={textColor}>Name</FormLabel>
                <Input
                  type="text"
                  value={recipientFormData.name}
                  onChange={(e) => {
                    setRecipientFormData({ ...recipientFormData, name: e.target.value });
                    if (formErrors.name) {
                      setFormErrors({ ...formErrors, name: '' });
                    }
                  }}
                  placeholder="Enter recipient name"
                  bg={badgeBg}
                  color={textColor}
                />
                {formErrors.name && (
                  <Text color="red.500" fontSize="xs" mt="5px">
                    {formErrors.name}
                  </Text>
                )}
              </FormControl>

              <FormControl isRequired isInvalid={!!formErrors.email}>
                <FormLabel color={textColor}>Email</FormLabel>
                <Input
                  type="email"
                  value={recipientFormData.email}
                  onChange={(e) => {
                    setRecipientFormData({ ...recipientFormData, email: e.target.value });
                    if (formErrors.email) {
                      setFormErrors({ ...formErrors, email: '' });
                    }
                  }}
                  placeholder="Enter recipient email"
                  bg={badgeBg}
                  color={textColor}
                />
                {formErrors.email && (
                  <Text color="red.500" fontSize="xs" mt="5px">
                    {formErrors.email}
                  </Text>
                )}
              </FormControl>

              <FormControl>
                <Flex align="center" justify="space-between">
                  <FormLabel color={textColor} mb="0">
                    Active Status
                  </FormLabel>
                  <Switch
                    isChecked={recipientFormData.is_active}
                    onChange={(e) => setRecipientFormData({ ...recipientFormData, is_active: e.target.checked })}
                    colorScheme="green"
                  />
                </Flex>
                <Text color={textColorSecondary} fontSize="xs" mt="5px">
                  Recipient will receive monitoring reports when active
                </Text>
              </FormControl>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onCreateClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleCreateRecipient}
              isLoading={creating}
              loadingText="Creating">
              Create Recipient
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Generate Monitor Link Modal */}
      <Modal isOpen={isLinkModalOpen} onClose={handleCloseLinkModal} size="lg">
        <ModalOverlay />
        <ModalContent bg={cardBg}>
          <ModalHeader color={textColor}>Generate Monitor Link</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {!generatedLink ? (
              <Flex direction="column" gap="20px">
                <Alert status="info" borderRadius="8px">
                  <AlertIcon />
                  <Text fontSize="sm">
                    Generate a one-time-use secure link to access the monitoring dashboard. The link will expire after the specified time.
                  </Text>
                </Alert>

                <FormControl isRequired>
                  <FormLabel color={textColor}>Expiration Time (minutes)</FormLabel>
                  <Input
                    type="number"
                    min="1"
                    max="1440"
                    value={linkExpiresIn}
                    onChange={(e) => {
                      const value = parseInt(e.target.value) || 60;
                      setLinkExpiresIn(Math.min(Math.max(1, value), 1440));
                      if (linkError) {
                        setLinkError(null);
                      }
                    }}
                    placeholder="Enter expiration time (1-1440 minutes)"
                    bg={badgeBg}
                    color={textColor}
                  />
                  <Text color={textColorSecondary} fontSize="xs" mt="5px">
                    Valid range: 1-1440 minutes (24 hours). Default: 60 minutes.
                  </Text>
                </FormControl>

                {linkError && (
                  <Alert status="error" borderRadius="8px">
                    <AlertIcon />
                    <Text fontSize="sm">{linkError}</Text>
                  </Alert>
                )}
              </Flex>
            ) : (
              <Flex direction="column" gap="20px">
                <Alert status="success" borderRadius="8px">
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="bold" mb="5px">Link Generated Successfully!</Text>
                    <Text fontSize="sm">
                      This is a one-time-use link. It will expire after {generatedLink.expires_in_minutes} minutes.
                    </Text>
                    {generatedLink.expires_at && (
                      <Text fontSize="sm" mt="5px">
                        Expires at: {formatDate(generatedLink.expires_at)}
                      </Text>
                    )}
                  </Box>
                </Alert>

                <FormControl>
                  <FormLabel color={textColor}>Generated Link</FormLabel>
                  <InputGroup>
                    <Input
                      type="text"
                      value={generatedLink.link || ''}
                      readOnly
                      bg={badgeBg}
                      color={textColor}
                      fontFamily="mono"
                      fontSize="sm"
                    />
                    <InputRightElement width="4.5rem">
                      <IconButton
                        h="1.75rem"
                        size="sm"
                        icon={<Icon as={MdContentCopy} />}
                        onClick={handleCopyLink}
                        aria-label="Copy link"
                      />
                    </InputRightElement>
                  </InputGroup>
                  <Text color={textColorSecondary} fontSize="xs" mt="5px">
                    Click the copy icon to copy the link to your clipboard
                  </Text>
                </FormControl>

                {generatedLink.note && (
                  <Alert status="warning" borderRadius="8px">
                    <AlertIcon />
                    <Text fontSize="sm">{generatedLink.note}</Text>
                  </Alert>
                )}
              </Flex>
            )}
          </ModalBody>
          <ModalFooter>
            {!generatedLink ? (
              <>
                <Button variant="ghost" mr={3} onClick={handleCloseLinkModal}>
                  Cancel
                </Button>
                <Button
                  colorScheme="blue"
                  onClick={handleGenerateLink}
                  isLoading={generatingLink}
                  loadingText="Generating">
                  Generate Link
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" mr={3} onClick={handleCloseLinkModal}>
                  Close
                </Button>
                <Button
                  colorScheme="blue"
                  onClick={handleCopyLink}
                  leftIcon={<Icon as={MdContentCopy} />}>
                  Copy Link
                </Button>
              </>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

